import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Banknote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FeesDashboardTab } from '../components/fees/FeesDashboardTab';
import { FeesTrackerTab } from '../components/fees/FeesTrackerTab';
import { RecordPaymentTab } from '../components/fees/RecordPaymentTab';
import { FeeStructureTab } from '../components/fees/FeeStructureTab';
import { ArrearsTab } from '../components/fees/ArrearsTab';
import { StatementsTab } from '../components/fees/StatementsTab';
import { FeesReportsTab } from '../components/fees/FeesReportsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolFeesPage() {
  const [config, setConfig] = useState(null);
  const [school, setSchool] = useState('suubi_primary');
  const [year, setYear] = useState(new Date().getFullYear());
  const [term, setTerm] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    axios.get(`${API_URL}/api/fees/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i);
  const ctx = { config, school, year, term, refreshKey, refresh };

  return (
    <div className="space-y-5" data-testid="school-fees-page">
      <div>
        <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <Banknote className="h-6 w-6 text-primary" /> School Fees Collection
        </h2>
        <p className="text-muted-foreground">Student payment tracking, installments, arrears and collection performance for both Suubi schools</p>
      </div>

      {/* School / Year / Term selectors */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Select value={school} onValueChange={setSchool}>
            <SelectTrigger className="w-72" data-testid="fees-school-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(config?.schools || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="fees-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(term)} onValueChange={(v) => setTerm(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="fees-term-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap h-auto" data-testid="fees-tabs">
          <TabsTrigger value="dashboard" data-testid="fees-tab-dashboard">Overview</TabsTrigger>
          <TabsTrigger value="tracker" data-testid="fees-tab-tracker">Payment Tracker</TabsTrigger>
          <TabsTrigger value="record" data-testid="fees-tab-record">Record Payment</TabsTrigger>
          <TabsTrigger value="structure" data-testid="fees-tab-structure">Fee Structure</TabsTrigger>
          <TabsTrigger value="arrears" data-testid="fees-tab-arrears">Arrears</TabsTrigger>
          <TabsTrigger value="statements" data-testid="fees-tab-statements">Statements</TabsTrigger>
          <TabsTrigger value="reports" data-testid="fees-tab-reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><FeesDashboardTab {...ctx} /></TabsContent>
        <TabsContent value="tracker" className="mt-4"><FeesTrackerTab {...ctx} /></TabsContent>
        <TabsContent value="record" className="mt-4"><RecordPaymentTab {...ctx} /></TabsContent>
        <TabsContent value="structure" className="mt-4"><FeeStructureTab {...ctx} /></TabsContent>
        <TabsContent value="arrears" className="mt-4"><ArrearsTab {...ctx} /></TabsContent>
        <TabsContent value="statements" className="mt-4"><StatementsTab {...ctx} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><FeesReportsTab {...ctx} /></TabsContent>
      </Tabs>
    </div>
  );
}
